<template lang="pug">
	v-card.pa-4
		v-row
			v-col(cols="12")
				h3(v-if="isDeliveryContact" class="subtitle-2") Alternate Delivery Contact
				h3(v-else-if="isCustomer" class="subtitle-2") Create Customer
			v-col(cols="6")
				v-text-field(
					v-model="formCustomer.firstName"
					label="First Name *"
					:rules="[rules.required]"
					clearable
					validate-on-blur
					@input="emitUpdate"
				)
			v-col(cols="6")
				v-text-field(
					v-model="formCustomer.lastName"
					label="Last Name *"
					:rules="[rules.required]"
					clearable
					validate-on-blur
					@input="emitUpdate"
				)
		v-row
			v-col(cols="12")
				v-select(
					v-model="formCustomer.customerType"
					:items="customerTypes"
					label="Customer Type *"
					:rules="[rules.required]"
					@change="emitUpdate"
				)
		
		template
			v-divider.pb-4
			v-row
				v-col(cols="12")
					v-text-field(
						v-model="formCustomer.email"
						label="Email (Optional)"
						:rules="[rules.email]"
						clearable
						validate-on-blur
						@input="emitUpdate"
					)
			v-row
				v-col(cols="4")
					v-text-field(
						v-model="formCustomer.phones[0].phoneNumber"
						label="Phone Number *"
						:rules="[rules.required]"
						clearable
						validate-on-blur
						@input="emitUpdate"
					)
				v-col(cols="4")
					v-select(
						v-model="formCustomer.phones[0].phoneType"
						:items="phoneTypes"
						label="Phone Type *"
						:rules="[rules.required]"
						@change="emitUpdate"
					)
				v-col(cols="4")
					v-checkbox(
						v-model="formCustomer.phones[0].isPrimary"
						label="Is Primary"
						@change="emitUpdate"
					)

			//- Address Section
			//- New Address button (always visible)
			v-row(justify="end")
				v-btn(
					@click="showAddressForm = !showAddressForm"
					class="mb-2 mr-4"
					color="primary"
					outlined
					small
				)
					| New Address
					v-icon(color="primary") {{ showAddressForm ? 'mdi-chevron-down' : 'mdi-plus' }}


			v-row(v-if="validAvailableCustomerAddresses && !showAddressForm")
				v-col(cols="1" class="d-flex justify-center")
					//- Edit button when addresses exist and form is closed
					v-btn(
						@click="showAddressForm = !showAddressForm"
						class="mt-4"
						color="primary"
						outlined
						small
						icon
					)
						v-icon mdi-pencil-circle-outline
				
				v-col(cols="11")
					v-autocomplete(
						v-model="formCustomer.address"
						:items="availableCustomerAddresses"
						:item-text="formatAddressLabel"
						return-object
						label="Select Customer Address"
					)

			v-row(v-if="validAvailableCustomerAddresses && !showAddressForm" align-center)
				v-col(cols="1")
					//- Empty spacer column to align with above row
				v-col(cols="4" class="d-flex align-center")
					v-checkbox(
						v-model="formCustomer.address.isPrimary"
						label="Is Primary"
						hide-details
						class="mt-0 pt-0"
					)
				v-col(cols="7")
					v-select(
						v-model="formCustomer.address.addressType"
						:items="addressTypeOptions"
						label="Address Type"
						outlined
						dense
						hide-details
						@change="emitUpdate"
					)

			//- Address form with cancel button when expanded
			v-row(v-if="showAddressForm")
				v-col(cols="1" class="d-flex justify-center")
					//- Cancel button when form is expanded
					v-btn(
						@click="showAddressForm = !showAddressForm"
						class="mt-4"
						color="error"
						outlined
						small
						icon
					)
						v-icon mdi-close-circle-outline
				
				v-col(cols="11")
					//- Indented address form
					div(class="pa-4")
						AddressForm(
							:handle-set-address="handleSetAddress"
							:show-save="false"
							:customer-addresses="availableCustomerAddresses"
							:primary-customer-address="formCustomer.address"
							@input="handleAddressFormInput"
						)
						
						//- Accept and Remove buttons at bottom right of form
						v-row(justify="end" class="mt-2")
							v-btn(
								v-if="validAvailableCustomerAddresses"
								@click="removeSelectedAddress"
								:disabled="!formCustomer.address"
								color="error"
								outlined
								small
								class="mr-2"
							)
								| Remove
							v-btn(
								@click="acceptAddressEdit"
								:color="canAcceptAddressForm ? 'success' : 'grey'"
								:disabled="!canAcceptAddressForm"
								outlined
								small
							)
								| Accept

			v-divider.pb-4

			v-row(v-if="showCreate")
				v-col.d-flex.justify-end
					v-btn(color="success" :disabled="!isCreateButtonEnabled" small outlined ripple @click="createCustomer();")
						v-icon mdi-content-save
						| Create
</template>

<script lang="ts">
	import Vue from 'vue';
	import AddressForm from '@/components/communication_center/shared/tabs/integration/address_form.vue';

	export default Vue.extend({
		data() {
			return {
				availableCustomerAddresses: [],
				customerTypes: [
					{ text: 'Individual', value: 'INDIVIDUAL' },
					{ text: 'Business', value: 'BUSINESS' },
				],
				formCustomer: {
					companyName: null,
					customerType: null,
					email: null,
					firstName: null,
					lastName: null,
					address: {
						addressLine1: null,
						addressLine2: null,
						city: null,
						state: null,
						zipCode: null,
						country: null,
						isPrimary: true,
					},
					phones: [{
						phoneNumber: null,
						phoneType: null,
						isPrimary: null,
					}],
				},
				showAddressForm: false,
				addressFormData: {
					addressLine1: '',
					addressLine2: '',
					city: '',
					state: '',
					zipCode: '',
					country: '',
					isPrimary: true,
				},
				phoneTypes: [
					{ text: 'Home', value: 'HOME' },
					{ text: 'Mobile', value: 'MOBILE' },
					{ text: 'Work', value: 'WORK' }
				],
				rules: {
					required: (v: any) => !!v || 'Required.',
					email: (v: any) => {
						if (!v) return true; // Allow empty/null/undefined values
						const pattern = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
						return pattern.test(v) || 'Invalid e-mail.'
					}
				},
			}
		},
		methods: {
			resetFormCustomer() {
				this.formCustomer = {
					companyName: null,
					customerType: null,
					email: null,
					firstName: null,
					lastName: null,
					address: {
						addressLine1: null,
						addressLine2: null,
						city: null,
						state: null,
						zipCode: null,
						country: null,
						isPrimary: true,
						addressType: null,
					},
					phones: [{
						phoneNumber: null,
						phoneType: null,
						isPrimary: null,
					}],
				};
				this.emitUpdate();
			},
			handleSetAddress(address) {
				this.formCustomer.address = address;
				this.showAddressForm = false;
				this.emitUpdate();
			},
			handleAddressFormInput(addressData) {
				// Store the address form data as it changes
				this.addressFormData = { ...addressData };
			},
			acceptAddressEdit() {
				if (!this.addressFormData) {
					return;
				}
				// Use the stored address form data
				const addressData = {
					addressLine1: this.addressFormData.addressLine1 || '',
					addressLine2: this.addressFormData.addressLine2 || '',
					city: this.addressFormData.city || '',
					state: this.addressFormData.state || '',
					zipCode: this.addressFormData.zipCode || '',
					country: this.addressFormData.country || '',
					isPrimary: this.addressFormData.isPrimary || true,
					addressType: this.addressFormData.addressType || '',
				};
				
				// Validate required fields
				if (!addressData.addressLine1 || !addressData.city || !addressData.state || !addressData.zipCode || !addressData.country) {
					return;
				}

				// if availableCustomerAddresses is not an array, initialize it
				if (!this.availableCustomerAddresses) {
					this.availableCustomerAddresses = [];
				}

				if (this.availableCustomerAddresses.find(addr => addr.addressLine1 === addressData.addressLine1 && addr.addressLine2 === addressData.addressLine2 && addr.city === addressData.city && addr.state === addressData.state && addr.zipCode === addressData.zipCode && addr.country === addressData.country)) {
					this.showSnackbar('error', 'Address already exists');
					return;
				}
				
				// Add the new address to the dropdown options
				this.availableCustomerAddresses.push(addressData);
				
				// Select the new address automatically
				this.formCustomer.address = addressData;
				
				// Close the form
				this.showAddressForm = false;
				
				// Emit update to parent
				this.emitUpdate();
			},
			removeSelectedAddress() {
				if (!this.formCustomer?.address || !this.availableCustomerAddresses) {
					return;
				}
				
				// Find the index of the selected address in the array
				const addressIndex = this.availableCustomerAddresses.findIndex(addr => addr.addressLine1 === this.formCustomer.address.addressLine1 && addr.addressLine2 === this.formCustomer.address.addressLine2 && addr.city === this.formCustomer.address.city && addr.state === this.formCustomer.address.state && addr.zipCode === this.formCustomer.address.zipCode && addr.country === this.formCustomer.address.country);
				
				if (addressIndex > -1) {
					// Remove the address from the dropdown options
					this.availableCustomerAddresses.splice(addressIndex, 1);

					// if another address exists, set the form customer address to that address instead or the first in the list
					if (this.availableCustomerAddresses.length > 0) {
						this.formCustomer.address = this.availableCustomerAddresses[0];
					} else {
						// clear the selected address
						this.formCustomer.address = null;
					}
					
					// Close the address form
					this.showAddressForm = false;
					
					// Emit update to parent
					this.emitUpdate();
				}
			},
			emitUpdate() {
				this.$emit('input', this.formCustomer);
			}
		},
		computed: {
			addressTypeOptions() {
				if (this.formCustomer?.customerType === 'BUSINESS') {
					return [
						{ text: 'Site', value: 'SITE' },
						{ text: 'Shipping', value: 'SHIPPING' },
						{ text: 'Billing', value: 'BILLING' },
					];
				} else {
					return [
						{ text: 'Shipping', value: 'SHIPPING' },
						{ text: 'Billing', value: 'BILLING' },
					];
				}
			},
			formatAddressLabel() {
				return (item) => `${item?.addressLine1 || "" } ${item?.addressLine2 || ""} ${item?.city || ""} ${item?.state || ""} ${item?.zipCode || ""} ${item?.country || ""}`;
			},
			validAvailableCustomerAddresses() {
				return this.availableCustomerAddresses?.length > 0;
			},
			isAddressFormValid() {
				// Check if all required address fields are filled
				return (this.addressFormData?.addressLine1 && 
						this.addressFormData?.city && 
						this.addressFormData?.state && 
						this.addressFormData?.zipCode && 
						this.addressFormData?.country);
			},
			hasAddressFormData() {
				// Check if user has entered any data in the address form
				return (this.addressFormData?.addressLine1 || 
						this.addressFormData?.addressLine2 || 
						this.addressFormData?.city || 
						this.addressFormData?.state || 
						this.addressFormData?.zipCode || 
						this.addressFormData?.country);
			},
			hasAddressFormChanged() {
				// Check if form data has changed from original address
				if (!this.formCustomer?.address) {
					// No original address, so any filled data is a change
					return (this.addressFormData?.addressLine1 || 
							this.addressFormData?.city || 
							this.addressFormData?.state || 
							this.addressFormData?.zipCode || 
							this.addressFormData?.country ||
							this.addressFormData?.isPrimary ||
							this.addressFormData?.addressType);
				}
				
				// Compare current form data with original address
				const original = this.formCustomer?.address;
				return (
					this.addressFormData?.addressLine1 !== original?.addressLine1 ||
					this.addressFormData?.addressLine2 !== original?.addressLine2 ||
					this.addressFormData?.city !== original?.city ||
					this.addressFormData?.state !== original?.state ||
					this.addressFormData?.zipCode !== original?.zipCode ||
					this.addressFormData?.country !== original?.country ||
					this.addressFormData?.isPrimary !== original?.isPrimary ||
					this.addressFormData?.addressType !== original?.addressType
				);
			},
			canAcceptAddressForm() {
				// Accept button should be green only when form is valid AND something has changed
				return this.isAddressFormValid && this.hasAddressFormChanged;
			},
			isCustomerFormValid() {
				// Local validation for customer form fields only
				const customer = this.formCustomer;
				
				// Check required customer fields
				if (!(customer?.firstName?.length > 0)) return false;
				if (!(customer?.lastName?.length > 0)) return false;
				// Email is optional
				if (!(customer?.phones?.[0]?.phoneNumber?.length > 0)) return false;
				if (!(customer?.phones?.[0]?.phoneType?.length > 0)) return false;
				if (!(customer?.customerType?.length > 0)) return false;
				
				// Ensure company name is present if customer type = BUSINESS
				if (customer?.customerType === 'BUSINESS') {
					if (!(customer?.companyName?.length > 0)) return false;
				}
				
				// Address is optional unless user is actively filling out address form
				// If address form is open and has data, it must be valid
				if (this.showAddressForm && this.hasAddressFormData && !this.isAddressFormValid) {
					return false;
				}
				
				return true;
			},
			shouldUseLocalValidation() {
				// Use local validation for customer creation, parent validation for appointment creation
				return this.isCustomer && !this.eligibleToSave;
			},
			isCreateButtonEnabled() {
				// Use local validation if this is customer creation, otherwise use parent validation
				return this.shouldUseLocalValidation ? this.isCustomerFormValid : this.eligibleToSave;
			},
		},
		components: {
			AddressForm,
		},
		mounted() {
			// set available customer addresses
			this.availableCustomerAddresses = this.customerAddresses;
		},
		props: {
			currentCampaignCustomer: Object,
			value: {
				type: Object,
				default: () => ({})
			},
			isDeliveryContact: Boolean,
			isCustomer: Boolean,
			handleCreate: Function,
			showCreate: Boolean,
			createCustomer: Function,
			eligibleToSave: Boolean,
			customerAddresses: Array,
			primaryCustomerAddress: Object,
			showSnackbar: Function,
		},
		watch: {
			value: {
				handler(newValue) {
					if (newValue && Object.keys(newValue).length > 0) {
						this.formCustomer = {...newValue};
					}
				},
				immediate: true,
				deep: true
			},
			'formCustomer.customerType': {
				handler(newValue: string) {
					if (newValue === 'INDIVIDUAL') {
						this.formCustomer.companyName = null;
					}
				}
			},
			sameAsCustomer: {
				handler(newValue: boolean) {
					if (newValue) {
						this.resetFormCustomer();
					}
				}
			},
		},
	});
</script>
